import React, { useEffect, useState } from 'react';
import {
	View,
	StyleSheet,
	TouchableOpacity,
	ScrollView,
	useWindowDimensions
} from 'react-native';
import SellscreenModal from './sellscreenModal';
import { ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast } from 'zen-core-elements';
import { ThemeConsumer } from '@rneui/themed';
import { Theme } from '@rneui/themed';
import ProductPlaceholder from './sellscreenProducts/productPlaceholder';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { addCartProduct, ensureVariantDiscounts, getProductDetailsById, updateCartProductQuantity } from '../../redux/slices/newSellscreenSlice';
import { config } from '../../shared/environment';
import CommonService from '../../shared/services/commonService';
// import { automaticDiscountType, buyXgetYDiscountType, productDataType, variantCombinationDetailsType } from '../sellScreen/productDetailsModule';
// import { getAutomaticOfferValue, getBuyXGetYOfferValue } from '../../redux/utils/discountUtils';
import { webDialogWidth } from '../../platform/design/dialogWeb';
import { productDataType, variantCombinationDetailsType } from '../sellScreen/productDetailsModule';
import { buildCartLine } from '../../redux/utils/discountUtils';
import { discountsForVariant, isCustomerEligible } from '../../redux/utils/discountEngine';
import { describeDiscount } from './discounts/discountLabels';


interface InfoChipProps {
	label: string;
	value: string;
	icon: string
}

const ModernProductModal = ({ visible, onClose, productId, isWrite }: { isWrite: boolean, visible: boolean, onClose: () => void, productId: number }) => {
	const { width, height } = useWindowDimensions()
	const { id: locationId, name: locationName } = useAppSelector((state) => state?.common?.currentLocation)
	const { cartProductIds, sellScreenType, customerDetails } = useAppSelector((state) => state?.NewSellscreenSlice)
	const [variantId, setVariantId] = useState<number>();
	const [quantity, setQuantity] = useState(1);
	const dispatch = useAppDispatch()

	const product = useAppSelector(((state) => state?.NewSellscreenSlice?.productsDetailsById[productId]));
	const discountStore = useAppSelector(((state) => state?.NewSellscreenSlice?.discountStore?.[locationId]));
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

	function getVariant(currProduct: productDataType) {
		return currProduct?.variantCombinationDetails[0] || currProduct?.productOptionMappings[0]?.option?.optionValues[0]?.productVariantMappings[0]?.variantCombinationDetail || undefined
	}

	useEffect(() => {
		if (visible) {
			getDetails()
			const variant = getVariant(product)
			setVariantId(variant?.id)
			setCartQuantity(variant)
		} else {
			setVariantId(undefined)
		}
	}, [productId, visible]);

	const getDetails = () => {
		dispatch(getProductDetailsById({
			productId,
			storeId: config?.storeId,
			locationId
		})).unwrap().then((res) => {
			const variant = getVariant(res?.productData || product)
			const oldVarient = getVariant(product)
			setVariantId(variant?.id)
			if (variant?.weightCount !== oldVarient?.weightCount) setCartQuantity(variant)
		})
	}

	const setCartQuantity = (variant: variantCombinationDetailsType) => {
		const cartQuantity = cartProductIds?.[locationId]?.[variant?.id]?.quantity || variant?.weightCount || 1
		setQuantity(cartQuantity)
	}

	useEffect(() => {
		if (variantId) {
			// Cached per variant; a variant already in the cart is not refetched
			dispatch(ensureVariantDiscounts({ productId, variantId, locationId }))
		}
	}, [variantId])


	const InfoChip: React.FC<InfoChipProps> = ({ label, value, icon }) => {
		return (
			<ThemeConsumer>
				{({ theme }) => {
					const styles = style(theme)
					return (
						<View style={styles.chipContainer}>
							<View style={styles.iconCircle}>
								<ZenIcon type='material-community' name={icon} size={16} color="#555" />
							</View>
							<View style={styles.textContainer}>
								<ZenText content={label.toUpperCase()} style={styles.label1} />
								<ZenText content={value} style={styles.value} />
							</View>
						</View>
					)
				}}
			</ThemeConsumer>
		);
	};

	const updateQuantity = ({ variantId, quantity }: { variantId: number; quantity: number }) => {
		dispatch(updateCartProductQuantity({ variantId, quantity, locationId }))
	}

	const addToCart = ({ variant, isAlreadyIncart }: { variant: variantCombinationDetailsType, isAlreadyIncart?: boolean }) => {
		if (!isWrite) {
			ZenToast({ message: "You don't have access!", type: "warning" })
			return
		}
		if (isAlreadyIncart) {
			const prevQuantity = cartProductIds?.[locationId]?.[variant?.id]?.quantity
			if (prevQuantity !== quantity) {
				updateQuantity({ variantId: variant?.id, quantity })
			}
		} else {
			dispatch(addCartProduct({ product: buildCartLine(product, variant, quantity), locationId }))
		}
		onClose()
	}

	const DetailsSection = () => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				const { name, variantCombinationDetails, productOptionMappings, isVariants } = product
				const optionValues = productOptionMappings[0]?.option?.optionValues || []
				const showColors = productOptionMappings[0]?.option?.showColors || false
				const currVaraint = isVariants ? optionValues?.find((item) => item?.productVariantMappings[0]?.variantCombinationDetail?.id === variantId)?.productVariantMappings[0].variantCombinationDetail : variantCombinationDetails[0]
				const stock = currVaraint?.multiLocationVariantStocks[0]?.stock || 0
				const isDiscountLoaded = Boolean(variantId && discountStore?.byVariant?.[variantId])
				// Expired offers (end date passed) are not shown
				const now = Date.now()
				const offers = variantId ? discountsForVariant(discountStore, variantId).filter((offer) => offer.endsAt == null || offer.endsAt >= now) : []
				const isAlreadyIncart = cartProductIds?.[locationId]?.[variantId || -1]?.productId ? true : false
				const isOutofStock = stock === 0 || stock === null || stock === undefined || stock < (currVaraint?.weightCount || 1)

				return (
					<>
						<ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollBody}>
							{/* Product Info Section */}
							<View style={styles.productRow}>
								<ProductPlaceholder name={name} style={{ height: 100, width: 100, borderRadius: 20 }} />
								<View style={styles.productInfo}>
									<ZenText content={name} style={styles.productName} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, currVaraint?.price?.toString() || "0")} style={styles.priceText} />
									{stock == -1 ? <View style={styles.stockBadge}>
										<ZenText content='Many In Stock' style={styles.stockText} />
									</View> : stock > 0 ?
										<View style={styles.stockBadge}>
											<ZenText content={`Stock: ${stock}`} style={styles.stockText} />
										</View> :
										<View style={styles.outOfStockBadge}>
											<ZenText content='Out of Stock' style={styles.outOfStockText} />
										</View>}
								</View>
								{(currVaraint?.sku || currVaraint?.id) ? <View style={styles.skuBadge}>
									<ZenText content={'SKU: #' + (currVaraint?.sku || currVaraint?.id)} style={styles.skuText} />
								</View> : null}
							</View>

							{/* Selection Section */}
							{optionValues?.length ?
								<>
									<ZenText content='Select Color' style={styles.label} />
									<View style={styles.chipGroup}>
										{optionValues?.map((option) => {
											const { id } = option?.productVariantMappings[0]?.variantCombinationDetail
											const isSelected = variantId === id
											return (
												<TouchableOpacity
													key={option?.id}
													onPress={() => setVariantId(id)}
													style={[
														styles.chip,
														isSelected && styles.chipActive
													]}
												>
													<View style={[styles.colorDot, { backgroundColor: showColors ? option?.color : (isSelected ? theme?.primary : theme?.disableTextColor) }]} />
													<ZenText content={option?.optionValue} style={[styles.chipLabel, isSelected && styles.chipLabelActive]} />
												</TouchableOpacity>
											)
										})}
									</View>
								</> : null}

							{/* Inventory Details Section */}
							<ZenText content='Inventory Details' style={styles.label} />
							<View style={{
								flexDirection: 'row',
								justifyContent: 'space-between',
								gap: 12,
								marginBottom: 20,
								paddingHorizontal: 4
							}}>
								<InfoChip
									label="Weight"
									value={currVaraint?.weightCount ? `${currVaraint?.weightCount} ${currVaraint?.weightCount > 1 ? "Units" : "Unit"}` : "N/A"}
									icon="weight-gram"
								/>
								<InfoChip
									label="Location"
									value={locationName || "N/A"}
									icon="map-marker-outline"
								/>
								<InfoChip
									label="UPC"
									value={currVaraint?.upc || "None"}
									icon="barcode-scan"
								/>
							</View>

							{/* Offers Section */}
							{isDiscountLoaded ?
								<>
									<ZenText content='Available Offers' style={styles.label} />
									{offers.length > 0 ?
										offers.map((offer) => {
											const { tag, title, description } = describeDiscount(offer, currencySymbol)
											const eligible = isCustomerEligible(offer, customerDetails?.customerId ?? null)
											const isAutomatic = offer.kind === "AUTOMATIC"
											return (
												<View style={[styles.offerItem, !eligible && { opacity: 0.55 }]} key={offer.id}>
													<View style={[styles.offerLeft, { flex: 1 }]}>
														<ZenText content='🏷️' style={styles.offerTag} />
														<View style={{ flex: 1 }}>
															<ZenText content={title} style={styles.offerTitle} />
															<ZenText content={eligible ? description : `${description} · Not available for ${customerDetails ? "this customer" : "walk-in customers"}`} style={styles.offerDesc} />
														</View>
													</View>
													<View style={isAutomatic ? styles.autoTag : styles.buyTag}><ZenText content={tag} style={isAutomatic ? styles.autoText : styles.buyText} /></View>
												</View>
											)
										})
										:
										<ZenEmptyComponent text="No Offers Found" viewStyle={{ marginTop: 10 }} />
									}
								</> :
								<ZenLoader viewStyle={{ backgroundColor: "transparent", flex: 1 }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
							}
						</ScrollView>
						{/* Bottom Actions */}
						{(isWrite && sellScreenType !== "RETURN") ? <View style={styles.footer}>
							{isOutofStock ?
								<View style={styles.outOfStock}>
									<ZenText content={'Out of Stock'} style={styles.outOfStockBtnText} />
								</View> :
								<>
									<View style={styles.counter}>
										<TouchableOpacity disabled={(quantity - (currVaraint?.weightCount || 1)) < 1} onPress={() => !isWrite ? ZenToast({ message: "You don't have access!", type: "warning" }) : setQuantity(quantity - (currVaraint?.weightCount || 1))} style={styles.countBtn}>
											<ZenIcon name='minus' type='antdesign' size={20} color={theme?.primary} />
										</TouchableOpacity>
										<ZenText content={quantity} style={styles.quantity} />
										<TouchableOpacity disabled={(quantity + (currVaraint?.weightCount || 1)) > (stock == -1 ? Infinity : stock)} onPress={() => !isWrite ? ZenToast({ message: "You don't have access!", type: "warning" }) : setQuantity(quantity + (currVaraint?.weightCount || 1))} style={styles.countBtn}>
											<ZenIcon name='plus' type='antdesign' size={20} color={theme?.primary} />
										</TouchableOpacity>
									</View>

									<TouchableOpacity style={styles.mainBtn} onPress={() => addToCart({ variant: currVaraint!!, isAlreadyIncart })}>
										<ZenText content={isAlreadyIncart ? 'Update Cart' : 'Add to Cart'} style={styles.mainBtnText} />
									</TouchableOpacity>
								</>}
						</View > : undefined}
					</>
				)
			}}
		</ThemeConsumer >
	)

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SellscreenModal isModalOpen={visible} setModalOpen={onClose}>
						<View style={[{ minWidth: width > 900 ? width * 0.5 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.70 : height * 0.50, }, webDialogWidth('lg', width)]}>

							{/* Header */}
							<View style={styles.header}>
								<ZenText content='Product Details' style={styles.headerTitle} />
								<TouchableOpacity onPress={onClose} style={styles.closeButton}>
									<ZenIcon name='close' type='antdesign' size={20} color={theme?.disabledGray500} />
								</TouchableOpacity>
							</View>

							{product ? <>
								<DetailsSection />
							</> :
								<ZenLoader viewStyle={{ backgroundColor: "transparent", flex: 1 }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
							}
						</View>
					</SellscreenModal>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(15, 23, 42, 0.6)', // Modern dark blue-ish tint overlay
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	modalContent: {
		width: '100%',
		maxWidth: 500, // Keeps it looking good on tablets
		backgroundColor: theme?.light,
		borderRadius: 24,
		padding: 24,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 12 },
		shadowOpacity: 0.15,
		shadowRadius: 24,
		elevation: 15,
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 20,
	},
	headerTitle: {
		fontSize: theme?.smallerFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
		letterSpacing: -0.5,
	},
	closeButton: {
		padding: 4,
		backgroundColor: theme?.softSlate,
		borderRadius: 20,
	},
	scrollBody: { paddingBottom: 10 },
	productRow: { flexDirection: 'row', marginBottom: 24 },
	productInfo: { marginLeft: 20, flex: 1, justifyContent: 'center' },
	productName: { fontSize: theme?.smallerFontSize, fontFamily: theme?.bold, color: theme?.lightDark },
	priceText: { fontSize: theme?.mediumFontSize, fontFamily: theme?.bold, color: theme?.primary, marginVertical: 4 },
	skuBadge: {
		backgroundColor: theme.lightPrimary,
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 8,
		alignSelf: 'flex-start',
		borderWidth: 1,
		borderColor: theme?.coolGray,
		marginTop: 10
	},
	skuText: { color: theme?.primary, fontSize: theme?.miniFontSize, fontFamily: theme?.medium },
	stockBadge: {
		backgroundColor: theme.lightTealGreen,
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 8,
		alignSelf: 'flex-start',
		borderWidth: 1,
		borderColor: theme?.coolGray,
	},
	stockText: { color: theme?.green, fontSize: theme?.miniFontSize, fontFamily: theme?.medium },
	outOfStockBadge: {
		backgroundColor: theme.red50,
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 8,
		alignSelf: 'flex-start',
		borderWidth: 1,
		borderColor: theme?.red10,
	},
	outOfStockText: { color: theme?.red500, fontSize: theme?.miniFontSize, fontFamily: theme?.medium },
	label: { fontSize: theme?.miniFontSize, fontFamily: theme?.medium, color: theme?.grey, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
	chipGroup: { flexDirection: 'row', gap: 12, marginBottom: 24 },
	chip: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 9,
		paddingHorizontal: 16,
		borderRadius: 14,
		borderWidth: 1,
		borderColor: theme?.coolGray,
		backgroundColor: theme?.light,
	},
	chipActive: { borderColor: theme?.primary, backgroundColor: theme?.tealPrimary },
	colorDot: { width: 9, height: 9, borderRadius: 6, marginRight: 8 },
	chipLabel: { fontSize: theme?.smallerFontSize, fontFamily: theme?.medium, color: theme?.disabledGray500 },
	chipLabelActive: { color: theme?.primary, fontFamily: theme?.bold },
	offerItem: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 16,
		backgroundColor: theme?.iceWhite,
		borderRadius: 16,
		borderWidth: 1,
		borderColor: theme?.softSlate,
		marginBottom: 10,
	},
	offerLeft: { flexDirection: 'row', alignItems: 'center' },
	offerTag: { fontSize: 14, marginRight: 12 },
	offerTitle: { fontSize: theme?.smallFontSize, fontFamily: theme?.medium, color: theme?.lightDark },
	offerDesc: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.disabledGray500 },
	autoTag: { backgroundColor: theme?.warmCream, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 0.6, borderColor: theme?.goldenOrange, },
	autoText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.goldenOrange },
	buyTag: { backgroundColor: theme?.blue50, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 0.6, borderColor: theme?.blue600, },
	buyText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.blue600 },
	inputBox: {
		backgroundColor: theme?.softSlate,
		borderRadius: 8,
		padding: 12,
		height: 100,
		textAlignVertical: 'top',
		fontSize: theme?.smallHeaderFontSize,
		color: theme?.lightDark,
		fontFamily: theme?.medium,
		marginBottom: 16,
	},
	footer: {
		flexDirection: 'row',
		marginTop: 20,
		gap: 16,
		alignItems: 'center',
	},
	counter: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme?.softSlate,
		borderRadius: 16,
		padding: 4,
	},
	countBtn: { width: 44, height: 44, justifyContent: 'center', alignItems: 'center' },
	quantity: { fontSize: theme?.mediumFontSize, fontFamily: theme?.bold, color: theme?.lightDark, paddingHorizontal: 10 },
	mainBtn: {
		flex: 1,
		backgroundColor: theme?.primary,
		paddingVertical: 13,
		borderRadius: 15,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: theme?.primary,
		shadowOffset: { width: 0, height: 6 },
		shadowOpacity: 0.2,
		shadowRadius: 10,
	},
	outOfStock: {
		flex: 1,
		backgroundColor: theme?.disableGray,
		paddingVertical: 13,
		borderRadius: 15,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: theme?.primary,
		shadowOffset: { width: 0, height: 6 },
		shadowOpacity: 0.2,
		shadowRadius: 10,
	},
	outOfStockBtnText: { color: theme?.red500, fontSize: theme?.smallerFontSize, fontFamily: theme?.bold },
	mainBtnText: { color: theme?.light, fontSize: theme?.smallerFontSize, fontFamily: theme?.bold },

	chipContainer: {
		flex: 1, // Ensures they take up equal space in a row
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme?.iceWhite,
		paddingVertical: 8,
		paddingHorizontal: 10,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: theme?.softSlate,
	},
	iconCircle: {
		width: 28,
		height: 28,
		borderRadius: 14,
		backgroundColor: theme?.light,
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: 8,
		// Soft shadow for the icon
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 2,
		elevation: 2,
	},
	textContainer: {
		flexShrink: 1,
	},
	label1: {
		fontSize: theme?.miniLabel,
		fontFamily: theme?.medium,
		color: theme?.grey,
		// letterSpacing: 0.5,
		marginBottom: 2,
	},
	value: {
		fontSize: theme?.miniFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
});

export default ModernProductModal;